<template>
  <BModal
    ref="modalMultipleTask"
    size="xl"
    scrollable
    centered
    hide-header
    no-close-on-esc
    no-enforce-focus
    no-close-on-backdrop
    modal-class="full-screen modal-no-padding"
    aria-hidden="false"
    role="dialog"
  >
    <div
      class="h-100"
      style="overflow-y: scroll; overflow-x: hidden;"
    >
      <ValidationObserver
        ref="formImportOfficerTask"
      >
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>
            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="attachments"
                name="attachments"
                rules="required"
              >
                <BFormGroup
                  label="Attachments"
                  label-for="fAttachments"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormFile
                    id="fAttachments"
                    v-model="formFile.attachments"
                    accept=".xls,.xlsx"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy || fetching.activities || fetching.crops || fetching.fsOfficers"
                    @change="validateExcelFile"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>
            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="schedule"
                name="schedule"
                rules="required"
              >
                <BFormGroup
                  label="Activity Month"
                  label-for="fActivityMonth"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fActivityMonth"
                    v-model="formImportOfficerTask.schedule"
                    type="month"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                    readonly
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>
          </BRow>
        </form>
      </ValidationObserver>
      <BTable
        small
        hover
        bordered
        responsive
        show-empty
        class="min-h-275px"
        :stacked="tableImportedOfficerTasks.stacked"
        :items="tableImportedOfficerTasks.rows"
        :fields="tableImportedOfficerTasks.fields"
      >
        <template #cell(index)="{ index }">
          {{ Number(index) + 1 }}
        </template>
      </BTable>
    </div>
    <template #modal-footer="{ cancel }">
      <BButton
        variant="primary"
        :disabled="state.busy"
        @click.prevent="validateImportTask"
      >
        Import Officer Tasks
      </BButton>
      <BButton

        variant="outline-dark"
        :disabled="state.busy"
        @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
      >
        Discard
      </BButton>
    </template>
  </BModal>
</template>
<script>
import { mapGetters } from 'vuex'
import { utils, writeFile } from 'data-validation-xlsx'
import MOMENT from 'moment'
import QUERY from 'lodash'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MFSOfficers from '@/mixins/accounts/fsos'
import MActivities from '@/mixins/maintenances/activities'

import { SASMFsoTask } from '@/services/asm'

export default {
  name: 'ASMTasksMultipleUploader',

  mixins: [
    MxMisc,
    MxFormatter,
    MActivities,
    MFSOfficers
  ],

  emits: [
    'imported'
  ],

  data () {
    return {
      state: {
        busy: false,
        downloading: false
      },
      selected: {
        team: {
          id: ''
        },
        activityType: 'Event'
      },
      formFile: {
        attachments: null
      },
      formImportOfficerTask: {
        schedule: '',
        file_name: '',
        overwrite: 0,
        tasks: []
      },
      tableImportedOfficerTasks: {
        stacked: 'sm',
        fields: [
          { key: 'index', label: '#' },
          { key: 'sso_id', label: 'Officer ID' },
          { key: 'officer_name', label: 'Officer Name', class: 'min-w-275px' },
          { key: 'activity_name', class: 'min-w-325px' },
          { key: 'event_plan', label: 'Number of Events', class: 'text-center' },
          {
            key: 'error',
            label: 'Line Error',
            class: 'text-danger min-w-350px',
            formatter: row => row?.error ? `${String(row?.column || '').toLowerCase()} ${row?.error || ''}` : ''
          }
        ],
        rows: [],
        errors: []
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    })
  },

  mounted () {
    if (this.currentUser) {
      this.selected.team.id = this.currentUser?.team || ''
    }
  },

  methods: {

    async downloadTemplate (selectedMonth) {
      if (this.state.downloading) {
        return
      }

      this.state.downloading = true

      return new Promise(resolve => {
        this.getFSOfficers(this, '', this.currentUser.team, this.currentUser.area).finally(() => {
          this.getActivities(this).finally(() => {
            const month = MOMENT(selectedMonth).format('YYYY-MM')

            const sheetProtection = {
              password: this.generateId(8), // optional
              selectLockedCells: true,
              selectUnlockedCells: true,
              formatCells: false,
              formatColumns: false,
              formatRows: false,
              insertColumns: false,
              insertRows: false,
              insertHyperlinks: false,
              deleteColumns: false,
              deleteRows: false,
              sort: false,
              autoFilter: false,
              pivotTables: false,
              objects: false,
              scenarios: false
            }

            const book = utils.book_new()

            const officerSheetData = [
              [
                'Officer ID',
                'Officer Name'
              ],
              ...this.options.fsOfficers.map(
                officer => [
                  officer.sso_id,
                  `${officer.first_name} ${officer.last_name}`
                ]
              )
            ]

            const officerSheet = utils.aoa_to_sheet(officerSheetData)

            officerSheet['!cols'] = [
              { wch: 15 }, // Officer ID
              { wch: 40 } // Officer Name
            ]

            officerSheet['!protect'] = sheetProtection

            const officerLength = this.options.fsOfficers.length

            const activitySheetData = [
              [
                'Activity Name'
              ],
              ...this.options.activities.map(
                activity => [
                  activity.activity_name
                ]
              )
            ]

            const activitySheet = utils.aoa_to_sheet(activitySheetData)

            activitySheet['!cols'] = [
              { wch: 50 } // Activity Name
            ]

            activitySheet['!protect'] = sheetProtection

            const activityLength = this.options.activities.length

            const officerTaskData = [
              [
                'Officer ID', // A
                'Officer Name', // B
                'Activity Name', // C
                'Number of Events' // E
              ],
              ...Array.from({ length: this.options.activities.length * this.options.fsOfficers.length }).map(
                () => ([
                  '',
                  '',
                  '',
                  0 // Number of Events
                ])
              )
            ]

            const officerTask = utils.aoa_to_sheet(officerTaskData)

            officerTask['!cols'] = [
              { wch: 15 }, // Officer ID
              { wch: 40 }, // Officer Name
              { wch: 50 }, // Activity Name
              { wch: 20 } // Number of Events
            ]

            officerTask['!dataValidation'] = [
              {
                type: 'list-in-sheet',
                values: `=Officers!A$2:A$${officerLength + 1}`,
                sqref: 'A2:A999'
              },
              {
                type: 'list-in-sheet',
                values: `=Activities!A$2:A$${activityLength + 1}`,
                sqref: 'C2:C999'
              }
            ]

            Array.from({ length: this.options.fsOfficers.length * this.options.activities.length }).forEach((_v, row) => {
              utils.sheet_set_array_formula(officerTask, `B${row + 2}`, `IFERROR(VLOOKUP(A${row + 2}, Officers!A$2:B$${officerLength + 1}, 2, FALSE), "")`)
            })

            utils.book_append_sheet(book, officerTask, `Tasks for ${MOMENT(month).format('MMMM YYYY')}`)
            utils.book_append_sheet(book, officerSheet, 'Officers')
            utils.book_append_sheet(book, activitySheet, 'Activities')

            const excelName = `${month}.xlsx`
            writeFile(book, excelName, { bookType: 'xlsx', type: 'binary' })
            this.state.downloading = false
            resolve(excelName)
          })
        })
      })
    },

    importTasks () {
      this.tableImportedOfficerTasks.rows = []
      this.tableImportedOfficerTasks.errors = []

      this.formImportOfficerTask = Object.assign(this.formImportOfficerTask, {
        schedule: '',
        file_name: '',
        overwrite: 0,
        tasks: []
      })

      this.getActivities(this)
      this.getFSOfficers(this, '', this.currentUser?.team, this.currentUser?.area)

      this.$refs.modalMultipleTask.show()
    },

    async validateFormFile (file) {
      return new Promise((resolve, reject) => {
        const extractPayloads = String(file.name).replace('.xlsx', '').replace('.xls', '').split('-')

        const validations = {
          swal: '<h6>File name should be in the format: <br /><b>YYYY-MM</b> <br /><br />Example: 2025-01.xlsx</h6>',
          input: 'File name should be in the format: YYYY-MM. Example: 2025-01.xlsx'
        }

        if (extractPayloads.length <= 1 || extractPayloads.length > 2) {
          return reject(validations)
        }

        const year = extractPayloads[0]
        const month = extractPayloads[1]

        const dateFormat = 'YYYY-MM'
        const originalDate = `${year}-${month}`

        const formattedDate = MOMENT(originalDate, dateFormat, true)

        if (!formattedDate.isValid()) {
          return reject(validations)
        }

        resolve({
          schedule: formattedDate.format(dateFormat),
          fileName: file.name
        })
      })
    },

    async validateExcelFile ({ target }) {
      this.formImportOfficerTask.tasks = []
      this.tableImportedOfficerTasks.rows = []
      this.tableImportedOfficerTasks.errors = []

      const file = QUERY.first(target.files)

      if (file) {
        this.validateFormFile(file).then(
          ({ schedule, fileName }) => {
            this.validateImportedMultiTask(file, this.options.activities, this.options.fsOfficers).then(
              sheetsWithRows => {
                const tasks = []
                const errors = []

                sheetsWithRows.forEach(
                  ({ rows, errors }) => {
                    errors.push(...errors)
                    rows.forEach(
                      (row, position) => {
                        tasks.push(Object.assign(row, {
                          schedule,
                          error: QUERY.find(errors, {
                            row: Number(position) + 2
                          })
                        }))
                      }
                    )
                  }
                )

                this.tableImportedOfficerTasks.rows = tasks
                this.tableImportedOfficerTasks.errors = errors

                this.formImportOfficerTask = Object.assign(this.formImportOfficerTask, {
                  file_name: fileName,
                  schedule,
                  tasks
                })
              }
            )
          }
        ).catch(
          ({ swal, input }) => {
            this.swalInvalid(swal, 'Invalid File Name Format').then(() => {
              this.$refs.formImportOfficerTask.setErrors({
                attachments: [input]
              })
            })
          }
        )
      }
    },

    validateImportTask () {
      const file = this.formFile.attachments
      if (file) {
        this.validateFormFile(file).then(() => {
          this.$refs.formImportOfficerTask.validate().then(
            validOfficerTask => {
              const invalidExcel = this.tableImportedOfficerTasks.errors.length > 0
              const invalidCards = this.formImportOfficerTask.tasks.length <= 0

              const currentMonth = MOMENT()
              const currentSchedule = MOMENT(this.formImportOfficerTask.schedule).isSameOrAfter(currentMonth, 'month')

              if (!validOfficerTask || invalidExcel || invalidCards || !currentSchedule) {
                if (!currentSchedule) {
                  this.$refs.formImportOfficerTask.setErrors({
                    schedule: [
                      `The activity month must be greater than or equal to ${currentMonth.format('MMMM YYYY')}`
                    ]
                  })
                }

                return this.swalInvalid()
              }

              this.swalConfirm({
                html: 'Import Officer OfficerTasks?',
                confirmButtonText: 'Import',
                preConfirm: () => {
                  return this.putImportTask().catch(
                    ({ message }) => {
                      if (message && message.errors) {
                        this.$refs.formImportOfficerTask.setErrors({
                          attachments: [message?.message]
                        })

                        this.$refs.formImportOfficerTask.setErrors(message.errors)
                      }
                    }
                  )
                }
              })
            }
          )
        }).catch(
          ({ swal, input }) => {
            this.swalInvalid(swal, 'Invalid File Name Format').then(
              () => {
                this.$refs.formImportOfficerTask.setErrors({
                  attachments: [input]
                })
              }
            )
          }
        )
      } else {
        this.$refs.formImportOfficerTask.setErrors({
          attachments: ['The attachments field is required.']
        })
      }
    },

    async putImportTask () {
      return SASMFsoTask.batchImport(this.formImportOfficerTask).then(
        ({ data }) => {
          this.swalSuccess(data.message).then(() => {
            this.$refs.modalMultipleTask.hide()

            this.$emit('imported')
          })
          return data
        }
      )
    }
  }
}
</script>
